import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  isLibraryDocumentIngestionStillProcessing,
  isLibraryDocumentIngestionTerminal,
} from "./cognitoryLibraryIngestionStatus";
import {
  resolveUploadLifecycleForDocument,
  resolveUploadScopedDocumentSummary,
  type UploadAttemptLifecycle,
} from "./cognitoryLibraryUploadLifecycle";

export const UPLOAD_INGESTION_POLL_INTERVAL_MS = 2500;
export const UPLOAD_INGESTION_POLL_MAX_ATTEMPTS = 80;

export type UploadIngestionPollResult =
  | { kind: "all_terminal" }
  | { kind: "timeout"; timedOutDocumentIds: string[] }
  | { kind: "aborted" };

export function classifyTrackedUploadIngestion(
  documents: GovernedLibraryDocumentSummary[],
  trackedDocumentIds: ReadonlySet<string>,
  trackedUploadLifecycles?: ReadonlyMap<string, UploadAttemptLifecycle>,
): {
  allTerminal: boolean;
  stillProcessingDocumentIds: string[];
} {
  const stillProcessingDocumentIds: string[] = [];

  for (const documentId of trackedDocumentIds) {
    const document = documents.find(entry => entry.documentId === documentId);
    if (!document) {
      stillProcessingDocumentIds.push(documentId);
      continue;
    }
    const lifecycle = resolveUploadLifecycleForDocument(
      documentId,
      trackedUploadLifecycles ?? new Map(),
    );
    const scopedDocument = resolveUploadScopedDocumentSummary(
      document,
      lifecycle,
    );
    if (
      !isLibraryDocumentIngestionTerminal({
        canonicalState: scopedDocument?.canonicalState,
        versionStatus: scopedDocument?.versionStatus ?? null,
      })
    ) {
      if (
        isLibraryDocumentIngestionStillProcessing({
          canonicalState: scopedDocument?.canonicalState,
          versionStatus: scopedDocument?.versionStatus ?? null,
        })
      ) {
        stillProcessingDocumentIds.push(documentId);
      }
    }
  }

  return {
    allTerminal: stillProcessingDocumentIds.length === 0,
    stillProcessingDocumentIds,
  };
}

export async function pollUploadIngestionUntilTerminal(input: {
  trackedDocumentIds: ReadonlySet<string>;
  trackedUploadLifecycles?: ReadonlyMap<string, UploadAttemptLifecycle>;
  fetchDocuments: () => Promise<GovernedLibraryDocumentSummary[]>;
  sleep: (ms: number) => Promise<void>;
  pollIntervalMs?: number;
  maxAttempts?: number;
  signal?: AbortSignal;
  onDocuments: (documents: GovernedLibraryDocumentSummary[]) => void;
}): Promise<UploadIngestionPollResult> {
  const pollIntervalMs =
    input.pollIntervalMs ?? UPLOAD_INGESTION_POLL_INTERVAL_MS;
  const maxAttempts = input.maxAttempts ?? UPLOAD_INGESTION_POLL_MAX_ATTEMPTS;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (input.signal?.aborted) {
      return { kind: "aborted" };
    }

    const documents = await input.fetchDocuments();
    input.onDocuments(documents);

    const classification = classifyTrackedUploadIngestion(
      documents,
      input.trackedDocumentIds,
      input.trackedUploadLifecycles,
    );
    if (classification.allTerminal) {
      return { kind: "all_terminal" };
    }

    if (input.signal?.aborted) {
      return { kind: "aborted" };
    }

    await input.sleep(pollIntervalMs);
  }

  const finalDocuments = await input.fetchDocuments();
  input.onDocuments(finalDocuments);
  const classification = classifyTrackedUploadIngestion(
    finalDocuments,
    input.trackedDocumentIds,
    input.trackedUploadLifecycles,
  );

  return {
    kind: "timeout",
    timedOutDocumentIds: classification.stillProcessingDocumentIds,
  };
}
